import { WifiOff, RotateCcw } from "lucide-react";
import Header from "../layout/Header";
import Footer from "../layout/Footer";

/**
 * Full-page "no internet" state shown when the browser goes offline (or when an
 * action fails because the connection dropped). Matches the app's dark theme
 * and keeps the Header/Footer so it reads as part of the site.
 */
type OfflineNoticeProps = {
  onRetry?: () => void;
};

export default function OfflineNotice({ onRetry }: OfflineNoticeProps) {
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    // Default: if we're back online, reload; otherwise just re-check.
    if (typeof navigator === "undefined" || navigator.onLine) {
      window.location.reload();
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[radial-gradient(circle_at_top_left,#252525_0,#050505_55%)] text-white">
      <Header />

      <main className="flex flex-1 flex-col items-center justify-center px-5 py-16 text-center">
        <div className="flex w-full max-w-[440px] flex-col items-center">
          <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full border border-white/15 bg-white/5">
            <WifiOff
              aria-hidden="true"
              strokeWidth={1.6}
              className="h-9 w-9 text-white/80"
            />
          </div>

          <h1 className="mb-3 text-[24px] font-bold sm:text-[28px]">
            You&apos;re offline
          </h1>

          <p className="mb-8 text-[14px] leading-relaxed text-white/70 sm:text-[15px]">
            We couldn&apos;t reach Vastavik because your device isn&apos;t
            connected to the internet. Check your connection and try again — your
            verification or upload will resume once you&apos;re back online.
          </p>

          <button
            type="button"
            onClick={handleRetry}
            className="inline-flex items-center justify-center gap-2 rounded-[8px] bg-white px-8 py-3 text-[15px] font-medium text-black transition hover:bg-gray-200"
          >
            <RotateCcw size={17} strokeWidth={2} />
            Try Again
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}
